import React, { useState } from 'react';
import { useLanguage } from '../i18n/LanguageProvider';
import { translations } from '../i18n/translations';
import CalendlyWidget from '../components/CalendlyWidget';

const FORM_NAME = 'contact';
const FORM_SUBJECT = 'New contact from agenttailor.ai (%{submissionId})';

type FormStatus = 'idle' | 'submitting' | 'success' | 'error';

const encodeFormData = (data: Record<string, string>): string =>
  Object.keys(data)
    .map((key) => `${encodeURIComponent(key)}=${encodeURIComponent(data[key])}`)
    .join('&');

const ContactPage: React.FC = () => {
  const { language } = useLanguage();
  const dict = translations[language].contactPage;
  const [formData, setFormData] = useState({
    name: '',
    company: '',
    email: '',
    message: '',
  });
  const [status, setStatus] = useState<FormStatus>('idle');

  const handleChange = (e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>) => {
    setFormData({
      ...formData,
      [e.target.name]: e.target.value,
    });
  };

  const handleSubmit = async (e: React.FormEvent<HTMLFormElement>) => {
    e.preventDefault();
    setStatus('submitting');

    try {
      const response = await fetch('/', {
        method: 'POST',
        headers: { 'Content-Type': 'application/x-www-form-urlencoded' },
        body: encodeFormData({
          'form-name': FORM_NAME,
          subject: FORM_SUBJECT,
          name: formData.name,
          company: formData.company,
          email: formData.email,
          message: formData.message,
          'bot-field': '',
        }),
      });

      if (!response.ok) {
        throw new Error('Form submission failed');
      }

      setStatus('success');
      setFormData({ name: '', company: '', email: '', message: '' });
    } catch {
      setStatus('error');
    }
  };

  return (
    <div className="py-16 md:py-24">
      {/* Hero Section */}
      <section className="bg-sand py-16 md:py-24">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center max-w-4xl mx-auto">
            <h1 className="text-4xl md:text-6xl font-bold text-navy mb-6 font-space">
              {dict.heroTitle}
            </h1>
            <p className="text-xl md:text-2xl text-slate mb-8 leading-relaxed">
              {dict.heroSub}
            </p>
          </div>
        </div>
      </section>

      {/* Contact Form & Info */}
      <section className="py-16 md:py-24 bg-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-16">
            {/* Contact Form */}
            <div>
              <h2 className="text-3xl font-bold text-navy mb-8 font-space">{dict.quickContact}</h2>

              <form
                name={FORM_NAME}
                method="POST"
                data-netlify="true"
                data-netlify-honeypot="bot-field"
                action="/"
                onSubmit={handleSubmit}
                className="space-y-6"
              >
                <input type="hidden" name="form-name" value={FORM_NAME} />
                <input
                  type="hidden"
                  name="subject"
                  value={FORM_SUBJECT}
                  data-remove-prefix
                />
                <p className="hidden">
                  <label>
                    Don&apos;t fill this out:
                    <input name="bot-field" />
                  </label>
                </p>

                <div>
                  <label htmlFor="name" className="block text-sm font-medium text-navy mb-2">
                    {dict.form.name}
                  </label>
                  <input
                    type="text"
                    id="name"
                    name="name"
                    required
                    value={formData.name}
                    onChange={handleChange}
                    disabled={status === 'submitting'}
                    className="w-full px-4 py-3 border border-slate/20 rounded-lg focus:ring-2 focus:ring-sky focus:border-transparent outline-none disabled:opacity-60"
                    placeholder={language === 'en' ? 'Your full name' : 'Uw volledige naam'}
                  />
                </div>

                <div>
                  <label htmlFor="company" className="block text-sm font-medium text-navy mb-2">
                    {dict.form.company}
                  </label>
                  <input
                    type="text"
                    id="company"
                    name="company"
                    value={formData.company}
                    onChange={handleChange}
                    disabled={status === 'submitting'}
                    className="w-full px-4 py-3 border border-slate/20 rounded-lg focus:ring-2 focus:ring-sky focus:border-transparent outline-none disabled:opacity-60"
                    placeholder={language === 'en' ? 'Your company name' : 'Uw bedrijfsnaam'}
                  />
                </div>

                <div>
                  <label htmlFor="email" className="block text-sm font-medium text-navy mb-2">
                    {dict.form.email}
                  </label>
                  <input
                    type="email"
                    id="email"
                    name="email"
                    required
                    value={formData.email}
                    onChange={handleChange}
                    disabled={status === 'submitting'}
                    className="w-full px-4 py-3 border border-slate/20 rounded-lg focus:ring-2 focus:ring-sky focus:border-transparent outline-none disabled:opacity-60"
                    placeholder={language === 'en' ? 'your.email@company.com' : 'uw.email@bedrijf.com'}
                  />
                </div>

                <div>
                  <label htmlFor="message" className="block text-sm font-medium text-navy mb-2">
                    {dict.form.question}
                  </label>
                  <textarea
                    id="message"
                    name="message"
                    rows={6}
                    required
                    value={formData.message}
                    onChange={handleChange}
                    disabled={status === 'submitting'}
                    className="w-full px-4 py-3 border border-slate/20 rounded-lg focus:ring-2 focus:ring-sky focus:border-transparent outline-none resize-vertical disabled:opacity-60"
                    placeholder={dict.form.placeholder}
                  />
                </div>

                {status === 'success' && (
                  <p className="rounded-lg bg-green-50 border border-green-200 px-4 py-3 text-green-800 text-sm" role="status">
                    {dict.form.success}
                  </p>
                )}

                {status === 'error' && (
                  <p className="rounded-lg bg-red-50 border border-red-200 px-4 py-3 text-red-800 text-sm" role="alert">
                    {dict.form.error}
                  </p>
                )}

                <button
                  type="submit"
                  disabled={status === 'submitting'}
                  className="w-full bg-sky text-navy px-6 py-4 rounded-lg font-semibold hover:bg-sky/90 transition-colors duration-200 disabled:opacity-60 disabled:cursor-not-allowed"
                >
                  {status === 'submitting' ? dict.form.sending : dict.form.submit}
                </button>
              </form>
            </div>

            {/* Contact Info & Discovery Call */}
            <div className="space-y-8">
              <div id="calendly">
                <h2 className="text-3xl font-bold text-navy mb-8 font-space">
                  {dict.discoveryTitle}
                </h2>
                <p className="text-slate mb-6 leading-relaxed">
                  {dict.discoveryText}
                </p>

                <div className="bg-sand rounded-xl p-8 mb-8">
                  <div className="text-center">
                    <CalendlyWidget url="https://calendly.com/tumsa-letif/30min?hide_event_type_details=1&hide_gdpr_banner=1&background_color=f1f5f9&text_color=0b2545&primary_color=38bdf8" minWidth="320px" height="700px" />
                  </div>
                </div>

                <div className="space-y-4 text-sm text-slate">
                  <div className="flex items-center">
                    <svg className="w-5 h-5 text-sky mr-3" fill="currentColor" viewBox="0 0 20 20">
                      <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zm3.707-9.293a1 1 0 00-1.414-1.414L9 10.586 7.707 9.293a1 1 0 00-1.414 1.414l2 2a1 1 0 001.414 0l4-4z" clipRule="evenodd" />
                    </svg>
                    <span>{dict.bullets[0]}</span>
                  </div>
                  <div className="flex items-center">
                    <svg className="w-5 h-5 text-sky mr-3" fill="currentColor" viewBox="0 0 20 20">
                      <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zm3.707-9.293a1 1 0 00-1.414-1.414L9 10.586 7.707 9.293a1 1 0 00-1.414 1.414l2 2a1 1 0 001.414 0l4-4z" clipRule="evenodd" />
                    </svg>
                    <span>{dict.bullets[1]}</span>
                  </div>
                  <div className="flex items-center">
                    <svg className="w-5 h-5 text-sky mr-3" fill="currentColor" viewBox="0 0 20 20">
                      <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zm3.707-9.293a1 1 0 00-1.414-1.414L9 10.586 7.707 9.293a1 1 0 00-1.414 1.414l2 2a1 1 0 001.414 0l4-4z" clipRule="evenodd" />
                    </svg>
                    <span>{dict.bullets[2]}</span>
                  </div>
                </div>
              </div>

              {/* Contact Information */}
              <div className="bg-navy rounded-xl p-8 text-sand">
                <h3 className="text-2xl font-bold mb-6 font-space">
                  {dict.infoTitle}
                </h3>
                <div className="space-y-4">
                  <div className="flex items-center">
                    <svg className="w-5 h-5 text-sky mr-3" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M3 8l7.89 4.26a2 2 0 002.22 0L21 8M5 19h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v10a2 2 0 002 2z" />
                    </svg>
                    <span>{dict.emailHint}</span>
                  </div>
                  <div className="flex items-start">
                    <svg className="w-5 h-5 text-sky mr-3 mt-0.5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M17.657 16.657L13.414 20.9a1.998 1.998 0 01-2.827 0l-4.244-4.243a8 8 0 1111.314 0z" />
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 11a3 3 0 11-6 0 3 3 0 016 0z" />
                    </svg>
                    <div>
                      {dict.location.split('\n').map((line, index) => (
                        <div key={index}>{line}</div>
                      ))}
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* FAQ Section */}
      <section className="py-16 md:py-24 bg-sand">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-16">
            <h2 className="text-3xl md:text-5xl font-bold text-navy mb-6 font-space">
              {dict.faqTitle}
            </h2>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-8 max-w-4xl mx-auto">
            {dict.faqs.map((faq, idx) => (
              <div key={idx} className="bg-white rounded-xl p-6">
                <h3 className="text-xl font-bold text-navy mb-3 font-space">{faq.q}</h3>
                <p className="text-slate">{faq.a}</p>
              </div>
            ))}
          </div>
        </div>
      </section>
    </div>
  );
};

export default ContactPage;
